import { View, Input, Button, Checkbox, Label } from "@/components/ui";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { InputFormControl } from "../input-form-control";
import { useEffect, useId } from "react";
import { FormInputPhoneNumber } from "../input-phone-number/controlled";
import { z } from "zod";
import { emailRE, nameRE } from "@/constants/regex";
import { zodResolver } from "@hookform/resolvers/zod";
import { Typography } from "../typography";
import { IconButton } from "@/components/ui/icon-button";
import { superRefinePhoneNumber } from "@/lib/helpers/phone-number-validation";

const contactSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .refine((value) => nameRE.test(value), {
      error: "Please avoid using special characters",
    }),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .refine((value) => nameRE.test(value), {
      error: "Please avoid using special characters",
    }),
  primary: z.boolean(),
  email: z.array(
    z.object({
      value: z.string().regex(emailRE, "Invalid email"),
    }),
  ),
  phoneNumber: z.array(
    z
      .object({
        value: z.string(),
      })
      .superRefine((arg, ctx) => {
        superRefinePhoneNumber({
          phoneNumber: arg.value,
          ctx,
          phoneNumberFieldName: "value",
        });
      }),
  ),
});

export type ContactFormData = z.input<typeof contactSchema>;
const defaultValues = {
  firstName: "",
  lastName: "",
  email: [{ value: "" }],
  phoneNumber: [{ value: "+65" }],
  primary: true,
};

export const AddAnotherContactModal = ({
  open,
  loading,
  setOpen,
  onSubmitContactForm,
}: {
  open: boolean;
  loading: boolean;
  onSubmitContactForm(contact: ContactFormData): void;
  setOpen(open: boolean): void;
}) => {
  const id = useId();

  const contactFormMethods = useForm<ContactFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(contactSchema),
  });
  const { control, reset, watch, handleSubmit } = contactFormMethods;

  const {
    fields: emailFields,
    append: appendEmailField,
    remove: removeEmailField,
  } = useFieldArray({
    control,
    name: "email",
  });
  const {
    fields: numberFields,
    append: appendNumberField,
    remove: removeNumberField,
  } = useFieldArray({
    control,
    name: "phoneNumber",
  });

  const watchEmails = watch("email");
  const watchPhoneNumbers = watch("phoneNumber");
  const onSubmitForm = handleSubmit(onSubmitContactForm);

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open]);

  return (
    <BottomDrawerModal open={open} onOpenChange={setOpen}>
      <FormProvider {...contactFormMethods}>
        <View>
          <Typography variant="label-lg" className="pb-6">
            Add Another Contact
          </Typography>
          <InputFormControl
            label="First Name"
            component={Input}
            name="firstName"
            control={control}
            componentProps={{
              placeholder: "John",
            }}
          />
          <InputFormControl
            label="Last Name"
            component={Input}
            name="lastName"
            control={control}
            componentProps={{
              placeholder: "Doe",
            }}
          />
          <View>
            {emailFields.map((field, index) => {
              return (
                <InputFormControl
                  key={field.id}
                  control={control}
                  name={`email.${index}.value`}
                  label="Email"
                  component={Input}
                  componentProps={{
                    placeholder: "example@gmail.com",
                  }}
                  rightRender={
                    watchEmails.length >= 2 ? (
                      <IconButton
                        iconName="minus"
                        onClick={() => removeEmailField(index)}
                      />
                    ) : (
                      <IconButton
                        iconName="plus"
                        onClick={() => appendEmailField({ value: "" })}
                      />
                    )
                  }
                />
              );
            })}
          </View>

          <View>
            {numberFields.map((field, index) => {
              return (
                <View
                  key={field.id}
                  className="flex flex-row items-end gap-2 pb-3"
                >
                  <View className="flex-1">
                    <FormInputPhoneNumber
                      name={`phoneNumber.${index}.value`}
                      label="Phone Number"
                    />
                  </View>
                  {watchPhoneNumbers.length >= 2 ? (
                    <IconButton
                      iconName="minus"
                      onClick={() => removeNumberField(index)}
                    />
                  ) : (
                    <IconButton
                      iconName="plus"
                      onClick={() =>
                        appendNumberField({
                          value: "+65",
                        })
                      }
                    />
                  )}
                </View>
              );
            })}
          </View>

          <View className="flex flex-row gap-2 py-6">
            <Controller
              name="primary"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id={id}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label className="text-sm text-muted-foreground" htmlFor={id}>
              Make default Contact
            </Label>
          </View>

          <Button
            variant="primary"
            color="CTA"
            fullWidth="full"
            rounded="full"
            onClick={onSubmitForm}
            loading={loading}
            children="Save"
          />
        </View>
      </FormProvider>
    </BottomDrawerModal>
  );
};
